import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Wallet, TrendingUp, Info, AlertCircle } from 'lucide-react';
import { useAccount, useNetwork } from 'wagmi';
import { getDexName, getDexVersion, getNativeTokenSymbol, isV3Supported } from '@/config/dex-v3-config';
import { useState, useEffect } from 'react';

interface MyPositionsProps {
  onLaunchPool?: () => void;
  onAddLiquidity?: () => void;
}

export function MyPositions({ onLaunchPool, onAddLiquidity }: MyPositionsProps) {
  const { address, isConnected } = useAccount();
  const { chain } = useNetwork();
  const [dexName, setDexName] = useState<string | null>(null);
  const [dexVersion, setDexVersion] = useState<'V2' | 'V3' | null>(null);
  const [nativeSymbol, setNativeSymbol] = useState('ETH');
  const [isV3, setIsV3] = useState(false);

  useEffect(() => {
    if (chain?.id) {
      setDexName(getDexName(chain.id));
      setDexVersion(getDexVersion(chain.id));
      setNativeSymbol(getNativeTokenSymbol(chain.id));
      setIsV3(isV3Supported(chain.id));
    }
  }, [chain?.id]);

  return (
    <div className="space-y-6">
      {/* Connection Status */}
      {!isConnected ? (
        <Alert className="border-yellow-500/50 bg-yellow-500/10">
          <AlertCircle className="h-4 w-4 text-yellow-500" />
          <AlertDescription>
            Please connect your wallet to view your liquidity positions.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-blue-500/50 bg-blue-500/10">
          <Info className="h-4 w-4 text-blue-500" />
          <AlertDescription>
            Viewing {dexName} {dexVersion} positions on <strong>{chain?.name}</strong>
          </AlertDescription>
        </Alert>
      )}

      {/* Positions Overview Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            My Liquidity Positions
          </CardTitle>
          <CardDescription>
            {isV3 
              ? 'Your V3 positions are represented as NFTs. Each position has a specific price range.'
              : 'Your V2 positions are represented as LP tokens. Liquidity is spread across all prices.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Empty State */}
          {isConnected && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
                <TrendingUp className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Positions Found</h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                You don't have any liquidity positions on {chain?.name} yet. 
                Create your first pool or add liquidity to start earning fees!
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={onLaunchPool}>
                  Launch New Pool
                </Button>
                <Button variant="outline" onClick={onAddLiquidity}>
                  Add to Existing Pool
                </Button>
              </div>
            </div>
          )}

          {/* Position List - Will be populated when we fetch real data */}
          {/* <div className="space-y-4">
            {positions.map((position) => (
              <PositionCard key={position.id} position={position} />
            ))}
          </div> */}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Value Locked</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$0.00</div>
            <p className="text-xs text-muted-foreground mt-1">Across all positions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Fees Earned (24h)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$0.00</div>
            <p className="text-xs text-muted-foreground mt-1">Unclaimed fees</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Active Positions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">0</div>
            <p className="text-xs text-muted-foreground mt-1">On {chain?.name || 'this network'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Info Alert */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Note:</strong> Position tracking is currently showing data for {chain?.name} only. 
          Switch networks to view positions on other chains.
        </AlertDescription>
      </Alert>
    </div>
  );
}
